import { useEffect, useMemo, useState } from "react";
import { MagnifyingGlass, CloudArrowDown, Warning, ArrowSquareOut, CheckSquare, Square } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtNum } from "@/lib/format";

export default function EPDs() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  // EC3 live connector state
  const [ec3Status, setEc3Status] = useState(null);
  const [ec3Open, setEc3Open] = useState(false);
  const [ec3Q, setEc3Q] = useState("");
  const [ec3Results, setEc3Results] = useState([]);
  const [ec3Loading, setEc3Loading] = useState(false);
  const [ec3Picked, setEc3Picked] = useState({});

  const refresh = async () => {
    const r = await api.get("/epds");
    setItems(r.data.items);
  };

  // eslint-disable-next-line
  useEffect(() => {
    refresh();
    api.get("/epds/ec3/status").then((r) => setEc3Status(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(items.map((i) => i.category))).sort()],
    [items],
  );

  const filtered = useMemo(
    () =>
      items.filter(
        (i) =>
          (cat === "All" || i.category === cat) &&
          (q.trim() === "" || i.name.toLowerCase().includes(q.toLowerCase())),
      ),
    [items, q, cat],
  );

  const runEc3Search = async () => {
    setEc3Loading(true);
    setEc3Results([]);
    try {
      const { data } = await api.get(`/epds/ec3/search?q=${encodeURIComponent(ec3Q)}&page_size=25`);
      setEc3Results(data.items || []);
      if (!data.items?.length) toast.info("No EC3 EPDs matched that query.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "EC3 search failed");
    } finally {
      setEc3Loading(false);
    }
  };

  const togglePick = (id) => setEc3Picked((p) => ({ ...p, [id]: !p[id] }));

  const importPicked = async () => {
    const ids = Object.entries(ec3Picked).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) return toast.error("Pick at least one EPD to import");
    try {
      const { data } = await api.post("/epds/ec3/import", { ec3_ids: ids });
      toast.success(`Imported ${data.imported} EC3 EPDs into your library`);
      setEc3Picked({});
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "EC3 import failed");
    }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · Global EPD Database"
        title="EPD Library"
        subtitle="Searchable Environmental Product Declarations across major regions and sources (EC3, IBU, EPD International)."
        kicker={`${items.length} MATERIALS · ${categories.length - 1} CATEGORIES`}
        guideId="epds"
        action={
          <button
            data-testid="ec3-connector-toggle"
            onClick={() => setEc3Open((o) => !o)}
            className="flex items-center gap-2 text-xs uppercase tracking-wider border border-gold/40 text-gold hover:bg-gold/10 px-3 py-1.5 rounded-sm transition-colors"
          >
            <CloudArrowDown size={14} /> EC3 Live Connector
          </button>
        }
      />

      {ec3Open && (
        <section data-testid="ec3-panel" className="mx-8 mt-6 border border theme-border theme-surface">
          <div className="px-6 pt-5 pb-4 border-b theme-border flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="overline">Building Transparency · EC3 API</div>
              <h3 className="font-serif-display text-2xl theme-ink mt-1">Pull live EPDs from EC3</h3>
              <p className="theme-ink-muted text-sm mt-2 max-w-2xl">
                Search the open EC3 database and import EPDs straight into your project. Imported EPDs join your local library and become available in the Calculator and Procurement views.
              </p>
            </div>
            <div className="text-right text-[11px] font-mono-data">
              {ec3Status?.configured ? (
                ec3Status.ok ? (
                  <span className="text-emerald-400">● connected · {ec3Status.key}</span>
                ) : (
                  <span className="text-amber-400">● needs business account</span>
                )
              ) : (
                <span className="theme-ink-dim">● not configured</span>
              )}
            </div>
          </div>

          {ec3Status && !ec3Status.ok && ec3Status.needs_business_account && (
            <div data-testid="ec3-upgrade-banner" className="mx-6 mt-4 border border-amber-500/30 bg-amber-500/5 p-4 rounded-sm">
              <div className="flex items-start gap-3">
                <Warning size={16} className="text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm theme-ink-muted leading-relaxed">
                  Your EC3 API key authenticates but is on a <strong className="theme-ink">private/restricted</strong> Developer account. EC3 requires a <strong className="theme-ink">business account</strong> for direct EPD pulls.
                  <div className="mt-2 text-xs theme-ink-dim">
                    Upstream message: <em>&ldquo;{ec3Status.upstream_message}&rdquo;</em>
                  </div>
                  <a
                    href={ec3Status.upgrade_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-gold hover:underline text-xs"
                  >
                    Manage EC3 apps & request upgrade <ArrowSquareOut size={11} />
                  </a>
                </div>
              </div>
            </div>
          )}

          <div className="px-6 py-4 flex gap-2 flex-wrap">
            <div className="flex-1 min-w-[260px] relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 theme-ink-dim" />
              <input
                data-testid="ec3-search-input"
                value={ec3Q}
                onChange={(e) => setEc3Q(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runEc3Search()}
                placeholder="Search EC3 (e.g. 'concrete C30', 'low-carbon steel')…"
                className="w-full theme-input border pl-9 pr-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-gold"
              />
            </div>
            <button
              data-testid="ec3-search-btn"
              onClick={runEc3Search}
              disabled={ec3Loading}
              className="text-xs uppercase tracking-wider bg-gold text-black hover:bg-gold/90 disabled:opacity-50 px-4 py-2.5 rounded-sm"
            >
              {ec3Loading ? "Searching…" : "Search EC3"}
            </button>
            <button
              data-testid="ec3-import-btn"
              onClick={importPicked}
              disabled={!Object.values(ec3Picked).some(Boolean)}
              className="text-xs uppercase tracking-wider border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-50 disabled:hover:bg-transparent px-4 py-2.5 rounded-sm"
            >
              Import selected
            </button>
          </div>

          {ec3Results.length > 0 && (
            <div className="px-6 pb-5 overflow-x-auto">
              <table className="w-full text-sm" data-testid="ec3-results-table">
                <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                  <tr>
                    <th className="w-10 px-3 py-3"></th>
                    <th className="text-left px-3 py-3">Material</th>
                    <th className="text-left px-3 py-3">Category</th>
                    <th className="text-left px-3 py-3">Manufacturer</th>
                    <th className="text-right px-3 py-3">GWP A1-A3</th>
                    <th className="text-left px-3 py-3">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {ec3Results.map((r) => (
                    <tr key={r.ec3_id} className="border-t border-white/5">
                      <td className="px-3 py-3">
                        <button onClick={() => togglePick(r.ec3_id)} data-testid={`ec3-pick-${r.ec3_id}`}>
                          {ec3Picked[r.ec3_id] ? (
                            <CheckSquare size={16} weight="fill" className="text-gold" />
                          ) : (
                            <Square size={16} className="theme-ink-dim" />
                          )}
                        </button>
                      </td>
                      <td className="px-3 theme-ink">{r.name}</td>
                      <td className="px-3 theme-ink-muted">{r.category}</td>
                      <td className="px-3 theme-ink-muted">{r.manufacturer || "—"}</td>
                      <td className="px-3 text-right font-mono-data text-gold">
                        {fmtNum(r.gwp_a1_a3, 1)} <span className="theme-ink-dim text-[11px]">{r.gwp_unit}</span>
                      </td>
                      <td className="px-3 text-xs">
                        {r.ec3_url ? (
                          <a href={r.ec3_url} target="_blank" rel="noreferrer" className="text-gold hover:underline inline-flex items-center gap-1">
                            EC3 <ArrowSquareOut size={10} />
                          </a>
                        ) : (
                          <span className="theme-ink-dim">EC3</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="px-8 pt-8 flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 theme-ink-dim" />
          <input
            data-testid="epd-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search materials, manufacturers, sources…"
            className="w-full theme-surface border border theme-border pl-9 pr-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-gold"
          />
        </div>
        <select
          data-testid="epd-category-filter"
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="theme-surface border border theme-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-gold"
        >
          {categories.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </section>

      <section className="px-8 pt-6 pb-12">
        <div className="border border theme-border theme-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
              <tr>
                <th className="text-left px-6 py-3">Material</th>
                <th className="text-left px-3 py-3">Category</th>
                <th className="text-left px-3 py-3">Manufacturer</th>
                <th className="text-left px-3 py-3">Region</th>
                <th className="text-right px-3 py-3">Unit</th>
                <th className="text-right px-6 py-3">A1–A3 factor</th>
                <th className="text-left px-3 py-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} data-testid={`epd-row-${m.id}`} className="border-t border-white/5 hover:bg-black/[0.02] dark:bg-white/[0.02]">
                  <td className="px-6 py-3 theme-ink">{m.name}</td>
                  <td className="px-3 theme-ink-muted">{m.category}</td>
                  <td className="px-3 theme-ink-muted">{m.manufacturer}</td>
                  <td className="px-3 font-mono-data theme-ink-muted">{m.region}</td>
                  <td className="px-3 text-right font-mono-data theme-ink-muted">{m.unit}</td>
                  <td className="px-6 py-3 text-right font-mono-data text-gold">
                    {fmtNum(m.factor_a1a3, 1)}
                    <span className="theme-ink-dim text-[11px] ml-1">kg/{m.unit}</span>
                  </td>
                  <td className="px-3 theme-ink-muted text-xs">{m.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
